"use client";

import { useEffect } from "react";
import { signOut } from "next-auth/react";
import { Clock, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

import { useAuth } from "@/context/auth-context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

const statusConfig = {
  PENDING: {
    title: "Application Pending Review",
    description: "We've received your application and will review it shortly.",
    icon: Clock,
    progress: 25,
    color: "text-yellow-500",
  },
  IN_PROGRESS: {
    title: "Application Under Review",
    description: "We're currently reviewing your application and documents.",
    icon: Clock,
    progress: 50,
    color: "text-blue-500",
  },
  SUBMITTED: {
    title: "Final Review Stage",
    description: "Your application is in the final review stage.",
    icon: CheckCircle2,
    progress: 75,
    color: "text-blue-500",
  },
  UNDER_REVIEW: {
    title: "Background Check in Progress",
    description: "We're completing your background check and verifying your documents.",
    icon: Clock,
    progress: 90,
    color: "text-blue-500",
  },
  REJECTED: {
    title: "Application Not Approved",
    description: "Unfortunately, your application was not approved at this time.",
    icon: XCircle,
    progress: 100,
    color: "text-red-500",
  },
};

export function TutorApprovalOverlay() {
  const { user } = useAuth();
  const status = (user?.onboardingStatus as string)?.toUpperCase?.() as keyof typeof statusConfig;
  
  if (!user || (user.role as string)?.toUpperCase?.() !== "TUTOR" || !status || (status as string) === "APPROVED") {
    return null;
  }

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <StatusIcon className={`h-8 w-8 ${config.color}`} />
            <CardTitle className="text-2xl">{config.title}</CardTitle>
          </div>
          <CardDescription className="text-base">
            {config.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Application Progress</span>
              <span>{config.progress}%</span>
            </div>
            <Progress value={config.progress} className="h-2" />
          </div>

          {status === "REJECTED" ? (
            <div className="bg-red-50 dark:bg-red-950/50 p-4 rounded-lg">
              <h4 className="font-semibold text-red-700 dark:text-red-300 mb-2">
                Next Steps
              </h4>
              <p className="text-sm text-red-600 dark:text-red-200">
                You can appeal this decision or submit a new application after 30 days.
                Please contact support if you have any questions.
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-950/50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">
                What's Next?
              </h4>
              <p className="text-sm text-blue-600 dark:text-blue-200">
                We'll notify you by email when there are updates to your application.
                This usually takes 2-3 business days.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => signOut({ callbackUrl: '/login' })}
            >
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 